import memoizeOne from "memoize-one";
import { roundValue, safeParseFloat } from "lib/number";
import { loopNestedChildren } from "./ag-grid-helper";
import { MergedColumnFormulaOptions } from "./types";
import { TOTAL_RETAIL as formulaTotalRetail } from "../formulas/formula-total-retail";
import { referenceMetricValueOrCompute } from "../sibling-value-getter";
import { ValueGetterParams } from "ag-grid-community";
import { MetricsKind } from "../types";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";

/**
 * Loop through row group children and recompute TOTAL_RETAIL value.
 * This is very useful when we can't re-use computed values because TOTAL_RETAIL metrics is not turned on, thus, the column doesn't exist.
 * @param factoryOptions
 * @param getterParams
 * @returns
 */
export const getChildrenTotalRetail = memoizeOne(
    (factoryOptions, getterParams) => {
        let sumValue = 0;
        const { node } = getterParams;
        loopNestedChildren(node, (childNode) => {
            const value = formulaTotalRetail({
                factoryOptions,
                getterParams: {
                    ...getterParams,
                    node: childNode,
                    data: childNode.data,
                },
            });

            sumValue += +(value ?? 0);
        });

        return sumValue;
    },
    (newArgs, oldArgs) =>
        newArgs[1].colDef.colId === oldArgs[1].colDef.colId && newArgs[1].node.id === oldArgs[1].node.id
);

/**
 * Get grand total retail of the row
 */
export const getGrandTotalRetail = (options: MergedColumnFormulaOptions, valueGetterParams: ValueGetterParams) => {
    const unitsValue = referenceMetricValueOrCompute(
        () => {
            let totalUnits = 0;
            loopNestedChildren(valueGetterParams.node, (childNode) => {
                totalUnits += getChildrenTotalRetail(
                    {
                        ...options,
                        deliveryId: null, // On purpose. Since this is to get grand total, `null` will count all deliveries
                        doors: Array.from(options.filteredDoorsForAssortmentSet.values()).map((door) => door), // On purpose. Since this is to get grand total, passing all doors being used will count all deliveries
                    },
                    {
                        ...valueGetterParams,
                        node: childNode,
                        data: childNode.data,
                    }
                );
            });

            return totalUnits;
        },
        valueGetterParams,
        MetricsKind.TOTAL_RETAIL,
        valueGetterParams.node.id,
        ColumnTypes.AllDimensionTotal
    );

    return unitsValue;
};

/**
 * All this function does is summing values from children rows. Therefore, there is no need to multiply units by retail price
 */
export const TOTAL_RETAIL = (values: number[], options: MergedColumnFormulaOptions) => {
    return roundValue(
        values.reduce((acc, value) => acc + safeParseFloat(value, 0), 0),
        options.decimal
    );
};
